import { FetchError } from '@execonline-inc/fetch.private';
import { InvalidUrlError } from '@execonline-inc/url';
import { Maybe } from 'maybeasy';
import { Task } from 'taskarian';
import { AppyError, callApi } from '../../../../Appy';
import { currentUserStore } from '../../../../CurrentUserStore';
import { MissingLinkError } from '../../../../Links';
import { findLinkT } from '../../../../LinkyLinky';
import { Link, Resource } from '../../../../Resource/Types';
import { fromNullableT } from '../../../../TaskExt';
import { TPlainTextKey } from '../../../../Translations';
import { AssistantRole } from '../../Types';
import { chatStore } from '../ChatStore';
import { chatMetadataResourceDecoder, updatedChatMetadataResourceDecoder } from './Decoders';

export type ChatContext = {
  assistantRole: AssistantRole;
  courseRegistrationId: Maybe<number>;
  licenseId: Maybe<number>;
};

export interface ProductLicense {
  id: number;
  name: string;
  quantityAvailable: number;
  startOn: Date;
  endOn: Date;
}

export type ProductLicenses = ReadonlyArray<ProductLicense>;

export interface ChatMetadata {
  defaultLicenseId: Maybe<number>;
  productLicenses: ProductLicenses;
}

export type ChatMetadataResource = Resource<ChatMetadata>;

export interface UpdatedChatMetadata {
  productLicenseId: number;
  stillAvailableRecommendationIds: Maybe<number[]>;
}

export type UpdatedChatMetadataResource = Resource<UpdatedChatMetadata>;

export interface Waiting {
  kind: 'waiting';
}

export interface Loading {
  kind: 'loading';
  chatContext: ChatContext;
}

export interface Ready {
  kind: 'ready';
  chatContext: ChatContext;
  chatMetadataResource: Maybe<ChatMetadataResource>;
}

export type ChatMetadataState = Waiting | Loading | Ready;

export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loading = (chatContext: ChatContext): Loading => ({
  kind: 'loading',
  chatContext,
});

export const ready = (chatMetadataResource: Maybe<ChatMetadataResource>, state: Loading): Ready => {
  const { chatContext } = state;

  chatContext.licenseId.elseDo(() =>
    chatMetadataResource.do(
      (resource) => (chatContext.licenseId = resource.payload.defaultLicenseId),
    ),
  );

  return {
    kind: 'ready',
    chatContext,
    chatMetadataResource,
  };
};

export interface Error {
  kind: 'error';
  message: TPlainTextKey;
}

export type ChatContextError = FetchError | MissingLinkError | AppyError | InvalidUrlError | Error;

export const fetchChatMetadataResource = (): Task<ChatContextError, ChatMetadataResource> =>
  Task.succeed<ChatContextError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(findLinkT('chat-metadata'))
    .andThen(callApi(chatMetadataResourceDecoder, {}));

const fillLinkTemplate = (link: Link): Task<ChatContextError, Link> =>
  fromNullableT(chatStore.correlationId).map((v) => ({
    ...link,
    href: link.href.replace('{chat_uuid}', `${v}`),
  }));

const updateLink = (links: ReadonlyArray<Link>): Task<ChatContextError, Link> =>
  Task.succeed<ChatContextError, ReadonlyArray<Link>>(links)
    .andThen(findLinkT('update-chat-metadata'))
    .andThen(fillLinkTemplate);

export const putProductLicense = (
  productLicenseId: number,
): Task<ChatContextError, UpdatedChatMetadataResource> =>
  Task.succeed<ChatContextError, ReadonlyArray<Link>>(currentUserStore.links)
    .andThen(updateLink)
    .andThen(
      callApi(updatedChatMetadataResourceDecoder, {
        product_license_id: productLicenseId,
      }),
    );
